/**
 * ProjectPrint — dedicated print-friendly view of a project for PDF export.
 *
 * Caleb wanted to print a project plan and give it to clients. This route
 * is opened in a new window from the "Print to PDF" button on the
 * ProjectDetail page. It renders the project header + calendar + Gantt
 * chart in a clean layout (no app sidebar, no nav chrome) and
 * auto-triggers window.print() shortly after load so the user gets the
 * native print dialog with "Save as PDF" as one of the destinations.
 *
 * Print CSS is scoped to this page only via the wrapper class so it
 * doesn't affect the rest of the app. Key choices:
 *   - @page is landscape — Gantt charts are wide and clients expect
 *     project plans to land in horizontal format
 *   - All interactive UI noise (buttons, sliders, popovers) gets hidden
 *     in print via the .print-hide utility we set on the on-screen
 *     toolbar. The Calendar/Gantt components themselves still render
 *     their normal markup but no dialogs pop up because we don't touch
 *     their state.
 *   - print-color-adjust: exact forces background colors to render
 *     (Tailwind's bg-blue-50 etc) so the calendar's holiday/weekend
 *     shading and the Gantt bar colors actually appear on paper
 *   - page-break-inside: avoid keeps the calendar grid and the Gantt
 *     section from being chopped across pages where possible
 */

import { useEffect, useMemo } from "react";
import { useRoute } from "wouter";
import { Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProject, type ProjectFull, type ProjectMilestone } from "@/hooks/useProjects";
import { useQuery } from "@tanstack/react-query";
import { CalendarView } from "@/components/projects/CalendarView";

// ─── Print-specific Gantt rendering ──────────────────────────────────
// The on-screen GanttView uses DAY_W=32px and assumes horizontal
// scrolling. For a 6-month project that is ~5760px wide, way too wide
// for a single landscape page (~1000px usable). We render a separate
// print-only Gantt that splits the timeline into chunks of DAYS_PER_PAGE
// days, each chunk on its own landscape page at a smaller-but-still-
// readable DAY_W. Bars that span multiple chunks get clipped per chunk
// and rendered on each page they touch.
// Tuned so the rendered Gantt fits inside a Letter landscape page at
// 0.4" margins (the worst case — A4 landscape is wider).
//   PRINT_DAY_W * DAYS_PER_PAGE + LABEL_COL_WIDTH = 14*56 + 160 = 944px
//   Letter landscape usable: 11" - 0.8" margins = 10.2" * 96dpi = 979px
//   A4 landscape usable:     11.69" - 0.8" margins ≈ 1043px
// Both fit with ~35px of headroom on Letter.
const PRINT_DAY_W = 14;     // px per day in print
const PRINT_ROW_H = 24;     // px per milestone row in print
const DAYS_PER_PAGE = 56;   // exactly 8 weeks per landscape page (clean week boundaries)
const LABEL_COL_WIDTH = 160; // px reserved for milestone name on the left of each row

const PRINT_PRIORITY_COLOR: Record<string, string> = {
  High: "#dc2626",
  Medium: "#d97706",
  Low: "#475569",
};

function dayDiff(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatChunkLabel(start: Date, end: Date): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

interface MilestonePlacement {
  milestone: ProjectMilestone;
  startDate: Date;
  endDate: Date; // exclusive end (start + duration)
}

function buildMilestonePlacements(milestones: ProjectMilestone[]): MilestonePlacement[] {
  return milestones
    .filter((m) => !!m.dueDate)
    .map((m) => {
      const start = startOfDay(new Date(m.dueDate!));
      const dur = Math.max(1, m.duration || 1);
      const end = new Date(start);
      end.setDate(end.getDate() + dur);
      return { milestone: m, startDate: start, endDate: end };
    })
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}

interface TimelineChunk {
  index: number;
  start: Date;
  end: Date; // exclusive
  daysInChunk: number;
}

function buildTimelineChunks(placements: MilestonePlacement[], projectStart: Date): TimelineChunk[] {
  if (placements.length === 0) return [];
  const earliest = startOfDay(
    new Date(Math.min(projectStart.getTime(), placements[0].startDate.getTime())),
  );
  const latest = startOfDay(
    new Date(Math.max(...placements.map((p) => p.endDate.getTime()))),
  );

  const chunks: TimelineChunk[] = [];
  let cursor = new Date(earliest);
  let i = 0;
  while (cursor < latest) {
    const chunkEnd = new Date(cursor);
    chunkEnd.setDate(chunkEnd.getDate() + DAYS_PER_PAGE);
    const actualEnd = chunkEnd > latest ? latest : chunkEnd;
    chunks.push({
      index: i++,
      start: new Date(cursor),
      end: new Date(actualEnd),
      daysInChunk: dayDiff(cursor, actualEnd),
    });
    cursor = chunkEnd;
  }
  return chunks;
}

// ─── Print Milestone Table ───────────────────────────────────────────
// Plain text reference for clients. Sorted by due date. Shows everything
// you need to read off the schedule without parsing the visual chart.
function PrintMilestoneTable({ project }: { project: ProjectFull }) {
  const sorted = useMemo(() => {
    return [...project.milestones]
      .filter((m) => m.dueDate)
      .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime());
  }, [project.milestones]);

  return (
    <div className="print-section bg-white p-6 mb-6 border rounded-lg">
      <h2 className="text-lg font-semibold mb-4">Milestone Schedule</h2>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b-2 border-foreground/20">
            <th className="text-left py-2 px-2 font-semibold">Milestone</th>
            <th className="text-left py-2 px-2 font-semibold">Owner</th>
            <th className="text-left py-2 px-2 font-semibold">Start</th>
            <th className="text-left py-2 px-2 font-semibold">End</th>
            <th className="text-left py-2 px-2 font-semibold">Days</th>
            <th className="text-left py-2 px-2 font-semibold">Priority</th>
            <th className="text-left py-2 px-2 font-semibold">Progress</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((m, idx) => {
            const start = new Date(m.dueDate!);
            const end = new Date(start);
            end.setDate(end.getDate() + (m.duration || 1));
            return (
              <tr
                key={m.id}
                className={idx % 2 === 0 ? "bg-muted/20" : ""}
                style={{ pageBreakInside: "avoid" }}
              >
                <td className="py-1.5 px-2 font-medium">{m.name}</td>
                <td className="py-1.5 px-2 text-muted-foreground">{m.owner || "—"}</td>
                <td className="py-1.5 px-2 whitespace-nowrap">
                  {start.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </td>
                <td className="py-1.5 px-2 whitespace-nowrap">
                  {end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </td>
                <td className="py-1.5 px-2 text-center">{m.duration || 1}</td>
                <td className="py-1.5 px-2">
                  <span
                    className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                    style={{
                      backgroundColor: PRINT_PRIORITY_COLOR[m.priority || "Low"],
                    }}
                  >
                    {m.priority || "Low"}
                  </span>
                </td>
                <td className="py-1.5 px-2">{m.progress || 0}%</td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted-foreground py-4">
                No scheduled milestones.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Print Gantt (one chunk = one page) ──────────────────────────────
function PrintGanttChunk({
  chunk,
  placements,
  totalChunks,
}: {
  chunk: TimelineChunk;
  placements: MilestonePlacement[];
  totalChunks: number;
}) {
  // Day axis labels — number every 7 days, plus month labels at month boundaries.
  const days: Array<{ date: Date; isMonthStart: boolean; weekNum: number | null }> = [];
  for (let i = 0; i < chunk.daysInChunk; i++) {
    const d = new Date(chunk.start);
    d.setDate(d.getDate() + i);
    days.push({
      date: d,
      isMonthStart: d.getDate() === 1 || i === 0,
      weekNum: i % 7 === 0 ? Math.floor(i / 7) + 1 : null,
    });
  }

  const gridWidth = chunk.daysInChunk * PRINT_DAY_W;
  const labelColumnWidth = LABEL_COL_WIDTH;
  const totalWidth = labelColumnWidth + gridWidth;

  // Filter milestones whose bar intersects this chunk's date range.
  const visiblePlacements = placements.filter(
    (p) => p.endDate > chunk.start && p.startDate < chunk.end,
  );

  return (
    <div
      className="print-section print-page-break bg-white p-6 mb-6 border rounded-lg"
      style={{ pageBreakBefore: "always", breakBefore: "page" }}
    >
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-lg font-semibold">
          Gantt — Page {chunk.index + 1} of {totalChunks}
        </h2>
        <p className="text-xs text-muted-foreground">{formatChunkLabel(chunk.start, chunk.end)}</p>
      </div>

      <div style={{ width: totalWidth, fontSize: "10px" }}>
        {/* Month strip */}
        <div className="flex border-b" style={{ marginLeft: labelColumnWidth }}>
          {days.map((d, i) => (
            <div
              key={i}
              style={{
                width: PRINT_DAY_W,
                borderLeft: d.isMonthStart ? "1px solid rgba(0,0,0,0.4)" : "none",
                paddingLeft: d.isMonthStart ? 2 : 0,
                fontSize: "9px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "visible",
              }}
            >
              {d.isMonthStart
                ? d.date.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
                : ""}
            </div>
          ))}
        </div>

        {/* Day-of-week + day-number strip */}
        <div className="flex border-b" style={{ marginLeft: labelColumnWidth }}>
          {days.map((d, i) => {
            const dow = d.date.getDay();
            const isWeekend = dow === 0 || dow === 6;
            return (
              <div
                key={i}
                style={{
                  width: PRINT_DAY_W,
                  fontSize: "8px",
                  textAlign: "center",
                  backgroundColor: isWeekend ? "#f1f5f9" : "transparent",
                  color: isWeekend ? "#94a3b8" : "#475569",
                  borderRight: "1px solid #e2e8f0",
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                {d.date.getDate()}
              </div>
            );
          })}
        </div>

        {/* Milestone rows */}
        {visiblePlacements.map((p) => {
          const m = p.milestone;
          // Clip the bar to this chunk's date range.
          const visibleStart = p.startDate < chunk.start ? chunk.start : p.startDate;
          const visibleEnd = p.endDate > chunk.end ? chunk.end : p.endDate;
          const offsetDays = dayDiff(chunk.start, visibleStart);
          const widthDays = Math.max(1, dayDiff(visibleStart, visibleEnd));
          const barColor = PRINT_PRIORITY_COLOR[m.priority || "Low"];
          const progressPct = m.progress || 0;

          return (
            <div
              key={m.id}
              className="flex items-center"
              style={{ height: PRINT_ROW_H, borderBottom: "1px solid #f1f5f9" }}
            >
              <div
                style={{
                  width: labelColumnWidth,
                  paddingRight: 8,
                  fontSize: "10px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={m.name}
              >
                {m.name}
              </div>
              <div style={{ position: "relative", width: gridWidth, height: PRINT_ROW_H }}>
                {/* Day grid background lines */}
                {days.map((d, i) => {
                  const dow = d.date.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        left: i * PRINT_DAY_W,
                        top: 0,
                        bottom: 0,
                        width: PRINT_DAY_W,
                        backgroundColor: isWeekend ? "rgba(241,245,249,0.5)" : "transparent",
                        borderRight: "1px solid #f1f5f9",
                      }}
                    />
                  );
                })}
                {/* Bar */}
                <div
                  style={{
                    position: "absolute",
                    left: offsetDays * PRINT_DAY_W,
                    top: 4,
                    width: widthDays * PRINT_DAY_W - 2,
                    height: PRINT_ROW_H - 8,
                    backgroundColor: barColor,
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  {/* Progress fill */}
                  {progressPct > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${progressPct}%`,
                        backgroundColor: "rgba(255,255,255,0.35)",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {visiblePlacements.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-6">
            No milestones in this date range.
          </div>
        )}
      </div>

      {/* Per-page legend */}
      <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: PRINT_PRIORITY_COLOR.High }} />
          High
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: PRINT_PRIORITY_COLOR.Medium }} />
          Medium
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: PRINT_PRIORITY_COLOR.Low }} />
          Low
        </span>
        <span>·</span>
        <span>Lighter overlay = % complete</span>
      </div>
    </div>
  );
}

function PrintGantt({ project }: { project: ProjectFull }) {
  const placements = useMemo(() => buildMilestonePlacements(project.milestones), [project.milestones]);
  const projectStart = useMemo(() => startOfDay(new Date(project.startDate)), [project.startDate]);
  const chunks = useMemo(() => buildTimelineChunks(placements, projectStart), [placements, projectStart]);

  if (chunks.length === 0) {
    return null;
  }

  return (
    <>
      {chunks.map((chunk) => (
        <PrintGanttChunk
          key={chunk.index}
          chunk={chunk}
          placements={placements}
          totalChunks={chunks.length}
        />
      ))}
    </>
  );
}

const PRINT_CSS = `
  /* Screen-only: render at a comfortable max-width for preview */
  body { background: #f5f5f5; }

  .project-print-shell {
    max-width: 1280px;
    margin: 0 auto;
    padding: 24px;
  }

  /* Print-only rules */
  @media print {
    @page {
      size: landscape;
      margin: 0.4in;
    }
    body, html {
      background: white !important;
      margin: 0;
      padding: 0;
      color-adjust: exact;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .project-print-shell {
      max-width: none;
      margin: 0;
      padding: 0;
    }
    /* Hide the on-screen toolbar */
    .print-hide { display: none !important; }

    /* Force background colors (Tailwind bg-* classes) to print */
    *, *::before, *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Anti-orphan rules for major sections */
    .print-section { page-break-inside: avoid; break-inside: avoid; }
    .project-header { page-break-after: avoid; }
    h1, h2, h3 { page-break-after: avoid; }

    /* Each big section starts on its own page so the layout is
       predictable: page 1 = header + calendar, page 2 = Gantt */
    .print-page-break { page-break-before: always; break-before: page; }

    /* Hide any inline-rendered popover/dialog content that might be
       attached to the DOM tree but invisible on screen. They sometimes
       leak into print output as empty rectangles. */
    [role="dialog"], [data-radix-popper-content-wrapper] { display: none !important; }
  }
`;

export default function ProjectPrint() {
  const [, params] = useRoute("/projects/:id/print");
  const { data: project, isLoading, isError } = useProject(params?.id);

  const { data: companies = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["companies-list"],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=500", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const clientName = project?.companyId
    ? companies.find((c) => c.id === project.companyId)?.name
    : null;

  // Auto-trigger the print dialog once the project data is loaded and
  // the components have had a chance to render. Delay is tuned so the
  // calendar's grid and the Gantt's positioned bars are fully laid out
  // before the print dialog snapshots the page. Without the delay, some
  // browsers print a partially-rendered chart.
  useEffect(() => {
    if (!project) return;
    const t = window.setTimeout(() => {
      window.print();
    }, 800);
    return () => window.clearTimeout(t);
  }, [project]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen text-muted-foreground">
        Project not found.
      </div>
    );
  }

  const startDate = new Date(project.startDate).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div className="project-print-shell">
        {/* On-screen toolbar — hidden in print */}
        <div className="print-hide flex items-center justify-between mb-6 pb-4 border-b">
          <div>
            <p className="text-sm text-muted-foreground">
              Print preview. Use your browser's print dialog to save as PDF
              (landscape, 0.4" margins recommended).
            </p>
          </div>
          <Button onClick={() => window.print()} className="gap-2">
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </Button>
        </div>

        {/* Document header — appears at top of page 1 */}
        <div className="project-header print-section bg-white p-6 mb-6 border rounded-lg">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              <img
                src="/outlook/first-turn-capital-logo.png"
                alt="First Turn Capital"
                className="h-14 w-auto rounded-lg bg-[#0D1B2A] p-2"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <div>
                <h1 className="text-2xl font-bold">{project.name}</h1>
                {clientName && (
                  <p className="text-base text-muted-foreground">{clientName}</p>
                )}
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground space-y-0.5">
              <p>
                <span className="font-medium text-foreground">Start date:</span> {startDate}
              </p>
              <p>
                <span className="font-medium text-foreground">Milestones:</span>{" "}
                {project.milestones.length}
              </p>
              <p>
                <span className="font-medium text-foreground">Generated:</span>{" "}
                {new Date().toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Calendar section — page 1 */}
        <div className="print-section bg-white p-6 mb-6 border rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Calendar</h2>
          <CalendarView project={project} />
        </div>

        {/* Milestone summary table — page 2.
            Plain text reference for clients. The Gantt below this is the
            visual; the table is the legible source of truth. */}
        <div className="print-page-break">
          <PrintMilestoneTable project={project} />
        </div>

        {/* Multi-page Gantt — splits the timeline into ~8-week chunks
            so a 5-6 month project prints across 3 pages instead of
            being squashed onto one unreadable page. Each chunk is its
            own page with a header, day grid, and the bar segments
            that fall within its date range. */}
        <PrintGantt project={project} />

        {/* Footer */}
        <div className="print-section text-center text-xs text-muted-foreground mt-6 pb-4">
          First Turn Capital · Generated from CRM ·{" "}
          {new Date().toLocaleString("en-US")}
        </div>
      </div>
    </>
  );
}
